<template>
    <div class="media-share" dusk="media-share-view-component">
        <layout :is-loading="isLoading" :sidebar-open="sidebar">

            <!-- bread crumbs //-->
            <template v-if="!isLoading" v-slot:breadcrumb>
                <li v-if="!isSingleSetShare"><router-link :to="{name: 'home', params: {id: item.id}}">{{ item.title }}</router-link></li>
                <li v-if="parentTitle"><router-link :to="getParentRoute()">{{ parentTitle }}</router-link></li>
                <li v-if="media" class="active"><span>{{ media.title }}</span></li>
            </template>

            <!-- Navbar actions //-->
            <template v-slot:navbar-right>
                <div v-show="selectedCount > 0" class="btn-group" id="select-multiple">
                    <button type="button" class="btn btn-default dropdown-toggle rounded-xl" id="with-selected" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" :disabled="isLoading">
                        With selected <span v-if="selectedCount > 0" class="badge">{{ selectedCount }}</span> <span class="caret"></span>
                    </button>
                    <ul class="dropdown-menu">
                        <li>
                            <a class="with-selected-download" @click.prevent="doDownloadSelected(item.id)"><i class="fas fa-cloud-download"></i> Download</a>
                        </li>
                        <li>
                            <a class="with-selected-clear" @click.prevent="doClearSelected"><i class="fa fa-ban"></i> Clear selected</a>
                        </li>
                    </ul>
                </div>

                <span class="sidebar-toggle text-muted" :class="{active: sidebar}" @click="toggleSidebar">
                    <span class="fa-stack fa-2x" content="Toggle sidebar" v-tippy="{arrow: true}">
                        <i class="fa-circle fa-stack-2x" :class="{fas: sidebar, far: !sidebar}"></i>
                        <i class="fas fa-stack-1x" :class="sidebarToggleClasses"></i>
                    </span>
                </span>

                <button class="btn btn-primary navbar-btn rounded-xl px-5" :disabled="!media" @click="doDownload(media)">
                    <i class="fas fa-cloud-download mr-2"></i> {{ media && media.isMediaSet ? 'Download all' : 'Download' }}
                </button>

                <router-link v-if="showBackBtn" :to="backToRoute" tag="button" class="btn btn-transparent btn-lg btn-close">
                    <i class="fas fa-times"></i>
                </router-link>
            </template>

            <!-- Main content //-->
            <div class="media-item" @mouseover="showSlideshowNav=true" @mouseleave="showSlideshowNav=false">
                <slide-show
                    v-if="hasItem && itemType !== 'MediaSetShare'"
                    :media="slideShowMedia" :currentItem="media"
                    :show="showSlideshowNav"
                    v-on:slideshow-nav="onSlideshowNav"
                />
                <component :is="itemType" :item="media" v-bind:selected="selectedItems" @update:selected="doItemSelected" />
            </div>

            <!-- Sidebar //-->
            <template v-slot:sidebar v-if="media">
                <div class="p-4">
                    <h3 class="m-t-10 m-b-10" v-html="media.title"></h3>
                    <p v-if="mediaDescription" v-html="mediaDescription"></p>

                    <media-location :item="media" :key="media.id" />
                    <media-info :item="media" />

                    <h4 class="header-title">Thumbnail</h4>
                    <div class="gallery-thumb-container">
                        <thumbnail :item="media" />
                        <media-icon v-if="media.type !== 'gallery'" :item="media" />
                    </div>
                </div>
            </template>

        </layout>
    </div>
</template>

<script>
import linkifyHtml from 'linkifyjs/html';
import { createNamespacedHelpers } from 'vuex';
import { upperFirst, map } from 'lodash';

// components
import Layout from './Layout';
import SlideShow from '@/components/media/SlideShow';
import MediaInfo from'@/components/media/info';
import MediaLocation from '@/components/media/MediaLocation';
import Thumbnail from '@/components/media/Thumbnail';
import MediaIcon from '@/components/media/MediaIcon';

// mixins
import types from '@/components/media/publish/media-view/item-type/types';
import download from '@/components/media/app/mixins/download';
import common from '@/components/media/app/mixins/common';

const { mapState } = createNamespacedHelpers('app/share');

export default {
    name: 'MediaView',
    mixins: [download, common],
    components: {
        Layout,
        SlideShow,
        MediaInfo,
        MediaLocation,
        Thumbnail,
        MediaIcon,
        ...types,
    },
    data() {
        return {
            media: false,
            mediaSet: false,
            showSlideshowNav: false,
        }
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.selectedItems = [];
            vm.$watch(vm1 => [vm.$route.params.id, vm.$route.params.parent_id, vm.$route.params.set_id, vm.$route.params.media_id], ids => {
                vm.getMediaItem();
            });
        })
    },
    mounted() {
        // escape key to close
        document.addEventListener('keyup', (e) => {
            if (e.keyCode === 27) {
                this.$router
                    .replace(this.backToRoute)
                    .catch(err => err);
            }
        });


    },
    watch: {
        hasItem: {
            immediate: true,
            handler(newValue) {
                if (newValue) this.getMediaItem();
            },
        },
    },
    computed: {
        ...mapState({
            sidebar: state => state.sidebar,
        }),
        mediaDescription() {
            let description = null;
            if (this.media && this.media.description) {
                description = linkifyHtml(this.media.description, {
                        defaultProtocol: 'https'
                });
            }
            return description;
        },
        isMediaView() {
            return this.$route.name === 'view';
        },
        isMediaSetView() {
            return this.$route.name === 'set-view';
        },
        itemType() {
            if (this.media) {
                let type = map(this.media.type.split('_'), upperFirst).join('');
                if (type === 'Image') type = 'Photo';
                if (type === 'BeforeAfter') type = `${type}Share`;
                if (type === 'Gallery') type = `MediaSetShare`;
                return type;
            }
        },
        sidebarToggleClasses() {
            return {
                'fa-arrow-to-left': !this.sidebar,
                'fa-arrow-to-right': this.sidebar,
                'fa-inverse': this.sidebar,
            }
        },
        slideShowMedia() {
            if (this.hasItem) {
                if (!this.$route.params.set_id && this.$route.params.media_id) {
                    return this.item.mediaItems;
                } else {
                    const set = this.findSet(this.item.mediaSets, this.$route.params.set_id);
                    return set.mediaItems;
                }
            }
        },
        backToRoute() {
            const route = {name: 'home', params: {id: this.item.id}};

            if (this.$route.params.set_id && this.$route.params.parent_id && !this.$route.params.media_id) {
                route.name = 'set-view';
                route.params.set_id = this.$route.params.parent_id;
                const set = this.findSet(this.item.mediaSets, this.$route.params.parent_id);
                if (set) {
                    if (set.hasParent) route.params.parent_id = set.parent.id;
                }
            } else if (this.$route.params.set_id && this.$route.params.parent_id && this.$route.params.media_id) {
                route.name = 'set-view';
                route.params.set_id = this.$route.params.set_id;
                const set = this.findSet(this.item.mediaSets, this.$route.params.set_id);
                if (set) {
                    if (set.hasParent) route.params.parent_id = set.parent.id;
                }
            } else if (this.$route.params.set_id && !this.$route.params.parent_id && this.$route.params.media_id) {
                route.name = 'set-view';
                route.params.set_id = this.$route.params.set_id;
            }

            return route;
        },
        parentTitle() {
            let title = false;
            if (this.media && this.media.isMediaSet && this.media.hasParent && !this.isRootSet(this.$route.params.set_id)) {
                title = this.media.parent.title;
            } else if (this.media && !this.media.isMediaSet) {
                const set = this.findSet(this.item.mediaSets, this.$route.params.set_id);
                if (set) {
                    title = set.title;
                }
            }
            return title;
        },
        showBackBtn() {
            return !this.isSingleSetShare || (this.isSingleSetShare && !this.isRootSet(this.$route.params.set_id)) || this.$route.params.media_id;
        },
    },
    methods: {
        toggleSidebar() {
            this.$store.commit('app/share/sidebar', !this.sidebar);
        },
        onSlideshowNav(index) {
            if (!this.$route.params.set_id && this.$route.params.media_id) {
                this.media = this.item.mediaItems[index];
                this.$router.push({name: 'media-view', params: { id: this.item.id, media_id: this.media.id }});
            } else if (this.$route.params.set_id && this.$route.params.media_id && !this.$route.params.parent_id) {
                const set = this.findSet(this.item.mediaSets, this.$route.params.set_id);
                this.media = set.mediaItems[index];
                const path = `/s/${this.item.id}/c/${set.id}/m/${this.media.id}`;
                this.$router.push({path});
            } else if (this.$route.params.set_id && this.$route.params.media_id && this.$route.params.parent_id) {
                const set = this.findSet(this.item.mediaSets, this.$route.params.set_id);
                this.media = set.mediaItems[index];
                const path = `/s/${this.item.id}/p/${set.parent.id}/c/${set.id}/m/${this.media.id}`;
                this.$router.push({path});
            }
        },
        getMediaItem() {
            // loading a media item from the root
            if (!this.$route.params.set_id && this.$route.params.media_id) {
                if (this.media = this.item.mediaItems.find(item => item.id === this.$route.params.media_id)) {
                } else if (this.media = this.item.mediaSets.find(item => item.id === this.$route.params.media_id)) {
                }
            // loading a media set view
            } else if (this.$route.params.set_id && !this.$route.params.parent_id && !this.$route.params.media_id) {
                this.media = this.item.mediaSets.find(item => item.id === this.$route.params.set_id);
            // loadimg a media nested media set view
            } else if (this.$route.params.set_id && this.$route.params.parent_id && !this.$route.params.media_id) {
                this.media = this.findSet(this.item.mediaSets, this.$route.params.set_id);

            // loading a media sets media item
            } else if (this.$route.params.set_id && this.$route.params.media_id) {
                const set = this.findSet(this.item.mediaSets, this.$route.params.set_id);
                this.media = set.mediaItems.find(item => item.id === this.$route.params.media_id);

            }
        },
        findSet(items, id) {
            if (!items) { return; }
            for (const item of items) {
                if (item.id === id) { return item; }
                const child = this.findSet(item.mediaSets, id);
                if (child) { return child; }
            }
        },
        isRootSet(id) {
            return this.hasItem && this.hasSets && this.item.mediaSets.find(i => i.id === id);
        },
        getParentRoute() {
            const route = {name: 'set-view', params: {id: this.item.id}};
            let parent_id  = null;

            if (this.$route.params.parent_id) {
                parent_id = this.$route.params.parent_id;
            } else {
                parent_id = this.$route.params.set_id;
            }

            const set = this.findSet(this.item.mediaSets, parent_id);

            if (set) {
                route.params.set_id = parent_id;
                if (set.hasParent) route.params.parent_id = set.parent.id;
            }

            return route;
        },
        doItemSelected(e) {
            const selected = this.selectedItems.map(i => i.item.id);
            if (!selected.includes(e.item.id)) {
                this.selectedItems.push(e);
            } else {
                this.selectedItems = this.selectedItems.filter(i => i.item.id !== e.item.id);
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.media-item {
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.item-type-image {
    position: relative;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: contain;
}

/* .item-type-before-after {
    position: relative;
    width: 100%;
    height: auto !important;
} */

.item-type-pdf {
    height: 100%;
}

.gallery-thumb-container {
    margin: 0;
}
</style>

<style lang="scss">
.video-responsive {
    position: relative;
    padding-bottom: 56.25%;
    height: 0;
    overflow: hidden;
    max-width: 100%;
    width: 100%;
}
.video-responsive iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}
</style>
